import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
} from '@mui/material';
import { SearchIcon } from 'lucide-react';
import { useNewProducts } from '../../../lib/query/hooks';
import { downloadCSV } from '../../../lib/utils';
import type { SimpleAccount } from '@/lib/types';

interface NewProductListProps {
  account: SimpleAccount;
}

export function NewProductList({ account }: NewProductListProps) {
  const [baseDate, setBaseDate] = useState<string>(() => {
    // デフォルトは今日の日付
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [searchBaseDate, setSearchBaseDate] = useState<string>(baseDate);

  // 新商品一覧を取得（searchBaseDateが変更されると自動的に再取得）
  const {
    data: newProducts = [],
    isLoading: loading,
    error,
  } = useNewProducts(account.code, searchBaseDate);

  // 検索ボタンクリック時
  const handleSearch = () => {
    if (!baseDate) {
      return;
    }
    setSearchBaseDate(baseDate);
  };

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    const csvData = newProducts.map((product) => ({
      SKUカスタマーID: product.customer_id,
      SKU名: product.name,
    }));

    const filename = `新商品一覧_${account.code}_基準日${searchBaseDate}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            新商品一覧
          </Typography>

          {/* 説明文 */}
          <Box
            sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
              📊 新商品一覧について
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              この一覧は、<strong>基準日を境界として新しく販売が開始されたSKU</strong>を表示します。
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>抽出条件：</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                基準日から425日前～基準日前日まで：<strong>販売実績なし</strong>
              </Typography>
              <Typography component="li" variant="body2">
                基準日～本日まで：<strong>販売実績あり</strong>
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
              ※ 425日は約14ヶ月の期間で、商品の季節性を考慮した期間設定です
            </Typography>
          </Box>

          {/* 検索フォーム */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
            <TextField
              label="基準日"
              type="date"
              value={baseDate}
              onChange={(e) => setBaseDate(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              helperText="この日付を境界として新商品を検索"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={<SearchIcon size={16} />}
            >
              検索
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error instanceof Error ? error.message : '新商品一覧の読み込みに失敗しました。'}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>新商品一覧を読み込み中...</Typography>
            </Box>
          )}

          {!loading && newProducts.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1">
                  検索結果: {newProducts.length}件 （基準日: {searchBaseDate}）
                </Typography>
                <Button variant="outlined" onClick={handleDownloadCSV} size="small">
                  CSVでダウンロード
                </Button>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  mt: 2,
                  overflowX: 'auto',
                  maxWidth: '100%',
                  '& .MuiTable-root': {
                    minWidth: 600,
                  },
                }}
              >
                <Table sx={{ whiteSpace: 'nowrap' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>SKUカスタマーID</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>SKU名</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newProducts.map((product, index) => (
                      <TableRow key={`new-product-${product.customer_id}-${index}`}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{product.customer_id}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{product.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {!loading && newProducts.length === 0 && !error && (
            <Alert severity="info">
              新商品データが見つかりませんでした。検索条件を変更してお試しください。
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
